
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';

interface LogoItem {
  url: string;
  alt_ar: string;
  alt_en: string;
}

interface LogoSettings {
  frontend_logo: LogoItem;
  admin_logo: LogoItem;
  favicon: { url: string };
}

const defaultLogos: LogoSettings = {
  frontend_logo: {
    url: '',
    alt_ar: '',
    alt_en: '',
  },
  admin_logo: {
    url: '',
    alt_ar: '',
    alt_en: '',
  },
  favicon: {
    url: '',
  },
};

export const useSiteLogo = (type: 'frontend' | 'admin' = 'frontend') => {
  const { i18n } = useTranslation();
  const [logos, setLogos] = useState<LogoSettings>(defaultLogos);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLogos();

    // الاستماع للتغييرات في الوقت الفعلي
    const subscription = supabase
      .channel('site_logos_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'site_settings',
          filter: 'setting_key=eq.site_logos',
        },
        (payload) => {
          if (payload.new && (payload.new as any).setting_value) {
            setLogos((payload.new as any).setting_value);
            // تحديث الـ favicon
            updateFavicon((payload.new as any).setting_value.favicon?.url);
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const updateFavicon = (url: string) => {
    if (!url) return;
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = url;
  };

  const loadLogos = async () => {
    try {
      const { data } = await supabase
        .from('site_settings')
        .select('*')
        .eq('setting_key', 'site_logos')
        .maybeSingle();

      if (data?.setting_value) {
        setLogos(data.setting_value);
        updateFavicon(data.setting_value.favicon?.url);
      } else {
        // التوافق مع الإعدادات القديمة
        const { data: oldData } = await supabase
          .from('site_settings')
          .select('*')
          .eq('setting_key', 'site_logo')
          .maybeSingle();

        if (oldData?.setting_value) {
          const oldLogo = oldData.setting_value;
          const migratedLogos: LogoSettings = {
            frontend_logo: oldLogo,
            admin_logo: oldLogo,
            favicon: { url: oldLogo.url },
          };
          setLogos(migratedLogos);
          updateFavicon(oldLogo.url);
        }
      }
    } catch (error) {
      console.error('Error loading logos:', error);
    } finally {
      setLoading(false);
    }
  };

  const logo = type === 'admin' ? logos.admin_logo : logos.frontend_logo;

  const getAlt = () => {
    return i18n.language === 'ar' ? logo.alt_ar : logo.alt_en;
  };

  return { 
    logo, 
    logos,
    loading, 
    getAlt,
    favicon: logos.favicon.url,
  };
};
